import { initializeApp, FirebaseApp } from 'firebase/app';
import {
  getMessaging,
  getToken,
  onMessage,
  isSupported,
  Messaging,
  MessagePayload,
} from 'firebase/messaging';
import { getPerformance, trace, FirebasePerformance } from 'firebase/performance';
import runtimeConfig, { FirebaseConfig } from 'config/runtimeConfig';

const SW_PATH = '/firebase-messaging-sw.js';

let appPromise: Promise<FirebaseApp | null> | null = null;
let messagingPromise: Promise<Messaging | null> | null = null;
let swRegistrationPromise: Promise<ServiceWorkerRegistration | null> | null = null;
let performancePromise: Promise<FirebasePerformance | null> | null = null;

const getFirebaseConfig = (): FirebaseConfig | null => runtimeConfig.FIREBASE ?? null;

export const isFirebaseMessagingAvailable = async (): Promise<boolean> => {
  if (!getFirebaseConfig()) return false;
  if (typeof window === 'undefined') return false;
  if (!('serviceWorker' in navigator) || !('Notification' in window)) return false;
  try {
    return await isSupported();
  } catch {
    return false;
  }
};

const getApp = (): Promise<FirebaseApp | null> => {
  if (!appPromise) {
    appPromise = (async () => {
      const cfg = getFirebaseConfig();
      if (!cfg) return null;
      return initializeApp(cfg);
    })();
  }
  return appPromise;
};

const getMessagingInstance = (): Promise<Messaging | null> => {
  if (!messagingPromise) {
    messagingPromise = (async () => {
      const available = await isFirebaseMessagingAvailable();
      if (!available) return null;
      const app = await getApp();
      if (!app) return null;
      return getMessaging(app);
    })();
  }
  return messagingPromise;
};

// Self-healing for users with a stale FCM service worker from a previous
// integration attempt: any /firebase-messaging-sw.js registration without our
// query-string fingerprint is from an older code path and would prevent the
// new registration from working. Unregister it before registering ours.
const cleanupStaleServiceWorkers = async (): Promise<void> => {
  if (!('serviceWorker' in navigator)) return;
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(
      regs.map(async (reg) => {
        const url = reg.active?.scriptURL ?? reg.waiting?.scriptURL ?? reg.installing?.scriptURL ?? '';
        if (url.includes('/firebase-messaging-sw.js') && !url.includes('firebaseConfig=')) {
          await reg.unregister();
        }
      }),
    );
  } catch (err) {
    console.warn('[firebase] stale SW cleanup failed:', err);
  }
};

const registerServiceWorker = (): Promise<ServiceWorkerRegistration | null> => {
  if (!swRegistrationPromise) {
    swRegistrationPromise = (async () => {
      const cfg = getFirebaseConfig();
      if (!cfg) return null;
      if (!('serviceWorker' in navigator)) return null;
      await cleanupStaleServiceWorkers();
      const { vapidKey: _ignored, ...firebaseClientConfig } = cfg;
      const encoded = btoa(JSON.stringify(firebaseClientConfig));
      const url = `${SW_PATH}?firebaseConfig=${encodeURIComponent(encoded)}`;
      const registration = await navigator.serviceWorker.register(url, { scope: '/' });
      await navigator.serviceWorker.ready;
      return registration;
    })();
  }
  return swRegistrationPromise;
};

const ensurePermission = async (): Promise<NotificationPermission> => {
  if (Notification.permission === 'granted' || Notification.permission === 'denied') {
    return Notification.permission;
  }
  return Notification.requestPermission();
};

export const requestFcmToken = async (): Promise<string | null> => {
  try {
    const cfg = getFirebaseConfig();
    if (!cfg) return null;
    const available = await isFirebaseMessagingAvailable();
    if (!available) return null;

    const permission = await ensurePermission();
    if (permission !== 'granted') return null;

    const [messaging, registration] = await Promise.all([
      getMessagingInstance(),
      registerServiceWorker(),
    ]);
    if (!messaging || !registration) return null;

    const token = await getToken(messaging, {
      vapidKey: cfg.vapidKey,
      serviceWorkerRegistration: registration,
    });
    return token || null;
  } catch (err) {
    console.error('[firebase] requestFcmToken failed:', err);
    return null;
  }
};

export const initializeFirebasePerformance = (): Promise<FirebasePerformance | null> => {
  if (!performancePromise) {
    performancePromise = (async () => {
      try {
        if (!getFirebaseConfig()) return null;
        if (typeof window === 'undefined') return null;
        // `yarn start` no reporta: las sesiones de desarrollo ensuciaban las métricas
        // de los entornos reales (localhost aparecía como un dominio más en la consola).
        if (process.env.NODE_ENV !== 'production') return null;
        const app = await getApp();
        if (!app) return null;
        return getPerformance(app);
      } catch (err) {
        console.error('[firebase] performance init failed:', err);
        return null;
      }
    })();
  }
  return performancePromise;
};

// Mismas reglas que valida el SDK en putAttribute(); record() no las aplica y un
// atributo inválido haría que el backend descartase la traza entera.
const ATTRIBUTE_NAME_REGEX = /^[a-zA-Z]\w{0,39}$/;
const MAX_ATTRIBUTE_VALUE_LENGTH = 100;
const MAX_CUSTOM_ATTRIBUTES = 5;

/**
 * Registra una traza personalizada ya medida (inicio + duración). No hace nada si
 * Performance no está disponible; en desarrollo la escribe en consola para poder
 * verificar la instrumentación sin enviar nada.
 *
 * Nunca meter datos de pacientes ni IDs en `name` ni en `attributes`: se envían a Google.
 */
export const recordTrace = (
  name: string,
  startTime: number,
  duration: number,
  attributes: Record<string, string> = {}
): void => {
  if (duration <= 0 || startTime <= 0) return;
  const validAttributes = Object.fromEntries(
    Object.entries(attributes)
      .filter(([key, value]) => ATTRIBUTE_NAME_REGEX.test(key) && value.length > 0)
      .slice(0, MAX_CUSTOM_ATTRIBUTES)
      .map(([key, value]) => [key, value.slice(0, MAX_ATTRIBUTE_VALUE_LENGTH)])
  );

  if (process.env.NODE_ENV !== 'production') {
    console.debug(`[perf] ${name} ${Math.round(duration)}ms`, validAttributes);
    return;
  }

  initializeFirebasePerformance()
    .then((perf) => {
      if (!perf) return;
      trace(perf, name).record(startTime, duration, { attributes: validAttributes });
    })
    .catch((err) => console.warn('[firebase] recordTrace failed:', err));
};

export const onForegroundMessage = async (
  handler: (payload: MessagePayload) => void
): Promise<() => void> => {
  const messaging = await getMessagingInstance();
  if (!messaging) return () => undefined;
  return onMessage(messaging, handler);
};
